import './styles/styles.scss';

import i18next from 'i18next';
import { version } from '../package.json';
import Preview from './Views/Preview';
import Main, { MainState } from './Views/Main';
import { AppProps } from './@types/AppProps';
import { State } from './@types/State';
import { useVwListner } from './Utils/vw';
import Summary from './Views/Summary';
import Modal from './Components/Modal/Modal';
import ConfirmClose from './Views/ConfirmClose';
import { createTheme } from './Utils/theme';
import { loadFont } from './Utils/font';
import { Vehicle } from './@types/Vehicle';
import i18n from './Utils/i18n';

const WAYKE_VALUATION_MODAL_ID = 'wayke-valuation-modal';

type Views = 'preview' | 'main' | 'summary';

const initialState: State = {
  registrationNumber: '',
  mileage: '',
  fname: '',
  lname: '',
  email: '',
  phone: '',
  branchId: '',
  description: '',
};

const createState = (props: AppProps): State => {
  const base = { ...initialState };
  return {
    ...base,
    branchId: props.branches[0].id,
  };
};

const pre = 'Invalid settings';
const verifySettings = (props: AppProps) => {
  if (!props.branches?.length) throw '${pre} - Missing "branches"';
  if (
    props.conditionReduction.veryGood === null ||
    props.conditionReduction.veryGood === undefined
  ) {
    throw `${pre} - Missing "conditionReduction.veryGood"`;
  }

  if (props.conditionReduction.good === null || props.conditionReduction.good === undefined) {
    throw `${pre} - Missing "conditionReduction.good"`;
  }

  if (props.conditionReduction.ok === null || props.conditionReduction.ok === undefined) {
    throw `${pre} - Missing "conditionReduction.ok"`;
  }
};

class App {
  private root?: HTMLElement;
  private readonly props: AppProps;
  private contexts: {
    modal?: Modal;
    unsubribeVwListner?: () => void;
  } = {};
  private state: State;
  private view: Views = 'preview';
  private vehicle?: Vehicle;
  private confirmClose: boolean = false;

  constructor(props: AppProps) {
    this.props = props;
    i18n(props.marketCode || 'SE');
    verifySettings(props);
    this.state = createState(this.props);

    if (!this.props.manual) {
      const createdRoot = document.createElement('div');
      createdRoot.className = 'waykevaluation-root';
      createdRoot.setAttribute('style', 'display: var(--root-display, none)');
      this.root = createdRoot;
      if (this.props.rootId) {
        const rootNode = document.getElementById(this.props.rootId);
        if (!rootNode) throw `Missing element with id '${this.props.rootId}'`;
        rootNode.appendChild(createdRoot);
      } else {
        document.body.appendChild(this.root);
      }

      createTheme(this.props.theme);
      loadFont(this.props.marketCode);
      this.root.dataset.version = version;
      this.root.dataset.market = props.marketCode;
      this.render();
    }
  }

  private onProceedPreview(nextState: State, vehicle: Vehicle) {
    this.state = nextState;
    this.view = 'main';
    this.vehicle = vehicle;
    this.render();
  }

  private onProceedMain(nextState: State) {
    this.view = 'summary';
    this.state = nextState;
    this.render();
  }

  private onEdit(state: MainState) {
    this.state = {
      ...state,
      registrationNumber: '',
    };
    this.view = 'main';
    this.vehicle = undefined;
    this.render();
  }

  private closeWithConfirm() {
    if (
      this.view !== 'main' ||
      (this.view === 'main' && !this.state.registrationNumber && this.props.manual)
    ) {
      this.close();
      return;
    }

    this.confirmClose = true;
    this.render();
  }

  private onAbortCloseWithConfirm() {
    this.confirmClose = false;
    this.render();
  }

  open() {
    if (!this.props.manual) throw "Can only call this method if 'manual' is set to true";
    const createdRoot = document.createElement('div');
    createdRoot.className = 'waykevaluation-root';
    createdRoot.setAttribute('style', 'display: var(--root-display, none)');
    this.view = 'main';
    this.root = createdRoot;
    if (this.props.rootId) {
      const rootNode = document.getElementById(this.props.rootId);
      if (!rootNode) throw `Missing element with id '${this.props.rootId}'`;
      rootNode.appendChild(createdRoot);
    } else {
      document.body.appendChild(this.root);
    }

    createTheme(this.props.theme);
    loadFont(this.props.marketCode);
    this.root.dataset.version = version;
    this.root.dataset.market = this.props.marketCode;
    this.render();
  }

  close() {
    this.confirmClose = false;
    this.contexts?.unsubribeVwListner?.();
    const registrationNumber = this.state.registrationNumber;

    if (this.props.manual) {
      this.view = 'main';
    } else {
      this.view = 'preview';
    }
    this.vehicle = undefined;
    this.state = createState(this.props);
    this.state.registrationNumber = registrationNumber;
    const lastChild = this.root?.lastChild;

    if (this.props.manual) {
      this.root?.remove();
    } else {
      if ((this.root?.childElementCount || 0) > 1 && lastChild) {
        this.root?.removeChild(lastChild);
      }
    }
  }

  private render() {
    if (!this.root) return;
    if (!this.contexts.unsubribeVwListner) {
      this.contexts.unsubribeVwListner = useVwListner();
    }

    this.contexts.modal = new Modal(this.root, {
      title: i18next.t('global.modalTitle', { brandName: i18next.t('global.brandName') }),
      id: WAYKE_VALUATION_MODAL_ID,
      logo: this.props.logo,
      logo2x: this.props.logo2x,
      market: this.props.marketCode,
      onClose: !this.confirmClose ? () => this.closeWithConfirm() : undefined,
    });

    if (this.confirmClose) {
      new ConfirmClose(this.contexts.modal.content, {
        onConfirmClose: () => this.close(),
        onAbortClose: () => this.onAbortCloseWithConfirm(),
      });
    } else {
      switch (this.view) {
        case 'preview':
          new Preview(this.root, {
            state: this.state,
            vehicle: this.vehicle,
            settings: this.props,
            onProceed: (nextState: State, vehicle: Vehicle) =>
              this.onProceedPreview(nextState, vehicle),
          });
          break;
        case 'main':
          new Main(this.contexts.modal.content, {
            state: this.state,
            vehicle: this.vehicle as Vehicle,
            settings: this.props,
            onUpdateVehicle: (vehicle?: Vehicle) => {
              this.vehicle = vehicle;
            },
            onProceed: (nextState: State) => this.onProceedMain(nextState),
            onEdit: (s: MainState) => this.onEdit(s),
          });
          break;
        case 'summary':
          new Summary(this.contexts.modal.content, {
            settings: this.props,
            state: this.state,
            onClose: () => this.close(),
          });
          break;
        default:
          throw 'Unknown view...';
      }
    }
  }
}

export default App;
